import type { DoctorProfile, Role, User } from '../types';
import { facilities, users } from './mockData';
import { addAudit, type Actor } from './auditService';
import { respond } from './respond';
import { collection, deleteField, doc, getDoc, getDocs, query, updateDoc, where } from 'firebase/firestore';
import { isReal } from './store';
import { callServer } from './server';

// Thrown where the action needs the server part (Cloud Functions, after the Blaze upgrade).
export const NEEDS_SERVER = 'NEEDS_SERVER';
const noUndef = <T>(v: T): T => JSON.parse(JSON.stringify(v));
import { auth, db, firebaseEnabled } from '../lib/firebase';

export async function getUsers(facilityId: string): Promise<User[]> {
  if (isReal()) {
    const snap = await getDocs(query(collection(db!, 'users'), where('facilityId', '==', facilityId)));
    return snap.docs.map((d) => ({ ...(d.data() as User), id: d.id })).sort((a, b) => a.name.localeCompare(b.name));
  }
  return respond(users.filter((u) => u.facilityId === facilityId));
}

export async function getDemoUser(role: Role, facilityId: string): Promise<User> {
  const u = users.find((x) => x.role === role && (role === 'super_admin' || x.facilityId === facilityId));
  if (!u) throw new Error(`No demo user for ${role}`);
  return respond(u);
}

export async function login(email: string, password: string): Promise<{ ok: boolean }> {
  return respond({ ok: email.length > 0 && password.length > 0 }, 400);
}

export async function requestPasswordReset(email: string): Promise<{ sent: boolean }> {
  if (firebaseEnabled && auth) {
    // Our own branded reset email (functions/src/passwordReset.ts forgotPassword), never Google's default one.
    // Always report "sent", whether or not the email exists, so the form can't be used to find accounts.
    try {
      await callServer('forgotPassword', { email: email.trim(), lang: document.documentElement.lang === 'hi' ? 'hi' : 'en' });
    } catch {
      /* ignore */
    }
    return { sent: true };
  }
  return respond({ sent: email.length > 0 }, 400);
}

// Signed-in user's own profile from Firestore (real mode).
export async function getUserProfile(uid: string): Promise<User> {
  if (!db) throw new Error('Firebase not configured');
  const snap = await getDoc(doc(db, 'users', uid));
  if (!snap.exists()) throw new Error('Profile not found');
  return { ...(snap.data() as User), id: snap.id };
}

export type AdminUserFields = Pick<User, 'name' | 'email' | 'phone' | 'role' | 'title' | 'specialty'>;

// Facility admin edits contact/role details; a doctor sees a notice when someone else changes their profile.
export async function updateUserByAdmin(id: string, v: AdminUserFields, actor?: Actor): Promise<void> {
  if (actor?.role !== 'facility_admin') throw new Error('Only facility admins can edit users');
  if (isReal()) {
    const u = await getUserProfile(id);
    const fields = (Object.keys(v) as (keyof AdminUserFields)[]).filter((k) => (u[k] ?? '') !== (v[k] ?? ''));
    if (!fields.length) return;
    // The role lives in the login token, so the server changes it.
    if (fields.includes('role')) await callServer('setUserRole', { uid: id, role: v.role });
    const other = fields.filter((k) => k !== 'role');
    const patch: Record<string, object | string | null> = noUndef(Object.fromEntries(other.map((k) => [k, v[k] ?? null])));
    if (id !== actor.id) patch.profileNotice = { by: actor.name, at: new Date().toISOString(), fields };
    await updateDoc(doc(db!, 'users', id), patch);
    addAudit(actor, 'user.updated', u.name, u.facilityId ?? undefined, fields.join(', '));
    return;
  }
  const u = users.find((x) => x.id === id)!;
  const fields = (Object.keys(v) as (keyof AdminUserFields)[]).filter((k) => (u[k] ?? '') !== (v[k] ?? ''));
  if (!fields.length) return respond(undefined);
  Object.assign(u, v);
  if (u.id !== actor.id) u.profileNotice = { by: actor.name, at: new Date().toISOString(), fields };
  addAudit(actor, 'user.updated', u.name, u.facilityId ?? undefined, fields.join(', '));
  return respond(undefined);
}

const LABEL: Record<string, string> = { qualification: 'qualification', regNo: 'registration number', council: 'council', hprId: 'HPR ID', signature: 'signature' };

export async function updateDoctorProfile(id: string, p: DoctorProfile, actor?: Actor): Promise<void> {
  if (actor?.id !== id || actor.role !== 'doctor') throw new Error('Only the doctor can edit their own profile');
  if (isReal()) {
    const u = await getUserProfile(id);
    const next = Object.fromEntries(Object.entries(p).map(([k, val]) => [k, typeof val === 'string' ? val.trim() || undefined : val])) as DoctorProfile;
    const changed = (Object.keys(next) as (keyof DoctorProfile)[]).filter((k) => (u.doctor?.[k] ?? '') !== (next[k] ?? ''));
    await updateDoc(doc(db!, 'users', id), { doctor: noUndef(next) });
    addAudit(actor, 'doctor.profile_updated', u.name, u.facilityId ?? undefined, changed.map((k) => LABEL[k]).join(', ') || 'no change');
    return;
  }
  const u = users.find((x) => x.id === id)!;
  const clean = Object.fromEntries(Object.entries(p).map(([k, val]) => [k, typeof val === 'string' ? val.trim() || undefined : val])) as DoctorProfile;
  const changed = (Object.keys(clean) as (keyof DoctorProfile)[]).filter((k) => (u.doctor?.[k] ?? '') !== (clean[k] ?? ''));
  u.doctor = clean;
  addAudit(actor, 'doctor.profile_updated', u.name, u.facilityId ?? undefined, changed.map((k) => LABEL[k]).join(', ') || 'no change');
  return respond(undefined);
}

export async function dismissProfileNotice(id: string): Promise<void> {
  if (isReal()) return updateDoc(doc(db!, 'users', id), { profileNotice: deleteField() });
  const u = users.find((x) => x.id === id);
  if (u) delete u.profileNotice;
  return respond(undefined);
}

export const profileStatus = (d?: DoctorProfile) => ({ qualification: !!d?.qualification, regNo: !!d?.regNo && !!d?.council, hprId: !!d?.hprId, signature: !!d?.signature });

// Yes/no only for super admin: never the values.
export async function getAbdmStatus(facilityId: string) {
  if (isReal()) {
    const [f, people] = await Promise.all([getDoc(doc(db!, 'facilities', facilityId)), getUsers(facilityId)]);
    return { hfr: !!f.data()?.hfrId, doctors: people.filter((u) => u.role === 'doctor').map((u) => ({ id: u.id, name: u.name, ...profileStatus(u.doctor) })) };
  }
  const f = facilities.find((x) => x.id === facilityId);
  return respond({ hfr: !!f?.hfrId, doctors: users.filter((u) => u.facilityId === facilityId && u.role === 'doctor').map((u) => ({ id: u.id, name: u.name, ...profileStatus(u.doctor) })) });
}
